import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Megaphone, Plus } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import type { Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import {
  EditorLanguages,
  LocalePicker,
  TranslationLinks,
  useContentLocale,
} from '../components/ContentLocale';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { ImageUpload } from '../components/ImageUpload';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { BannerDetail, BannerList, Category } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

type Placement = BannerDetail['placement'];
type Audience = BannerDetail['audience'];
const PLACEMENTS: Placement[] = ['home_hero', 'home_secondary', 'category_top', 'announcement', 'popup'];
const AUDIENCES: Audience[] = ['all', 'retail', 'dealer'];
const IMAGE_PLACEMENTS: Placement[] = ['home_hero', 'home_secondary', 'category_top'];

const stateTone: Record<BannerList[number]['state'], Tone> = {
  draft: 'neutral',
  scheduled: 'amber',
  live: 'moss',
  expired: 'slate',
  inactive: 'slate',
};

/** `datetime-local` alanı için yerel saat (saniyesiz). */
function toLocalInput(v: string | Date | null): string {
  if (!v) return '';
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

// ---------------------------------------------------------------------------- Liste

/** Bannerlar: ana sayfa slider'ı ve ikincil alan, kategori üstü, duyuru çubuğu, açılır pencere. */
export function BannersPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canPublish = can('content.publish');
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const list = useQuery({
    queryKey: ['banners', storeId, locale],
    queryFn: () => api.get<BannerList>(`/v1/admin/banners?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  const reorder = useApiMutation(
    (v: { placement: Placement; ids: string[] }) =>
      api.post<BannerList>('/v1/admin/banners/order', { storeId, locale, ...v }),
    { invalidate: [['banners', storeId]], success: t('admin.banners.reordered') },
  );
  if (loading) return <Spinner label={t('common.loading')} />;
  const move = (placement: Placement, items: BannerList, i: number, dir: -1 | 1) => {
    const ids = items.map((b) => b.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate({ placement, ids });
  };
  return (
    <PageFrame
      helpKey="page.banners"
      actions={
        can('content.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: '/bannerlar/$id', params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.banners.create')}
            </Button>
            <HelpTip k="banners.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : (
            PLACEMENTS.map((placement) => {
              const items = list.data.filter((b) => b.placement === placement);
              return (
                <Section
                  key={placement}
                  title={t(`admin.banners.placements.${placement}`)}
                  description={t(`admin.banners.placementLead.${placement}`)}
                >
                  {!items.length ? (
                    <p className="text-sm text-ink-3">{t('admin.banners.emptyPlacement')}</p>
                  ) : (
                    <ol className="divide-y divide-line rounded-md border border-line bg-surface">
                      {items.map((b, i) => (
                        <li
                          key={b.id}
                          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm"
                        >
                          <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-line bg-surface-2">
                            {b.desktopFileId ? (
                              <img
                                src={`/v1/files/${b.desktopFileId}/content`}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Megaphone className="h-5 w-5 text-ink-3" aria-hidden />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <Link
                              to="/bannerlar/$id"
                              params={{ id: b.id }}
                              className="font-medium text-pine hover:underline"
                            >
                              {b.name}
                            </Link>
                            <p className="truncate text-ink-3">
                              {[b.title, b.text].filter(Boolean).join(' — ') || t('admin.banners.noText')}
                            </p>
                            {(b.startsAt || b.endsAt) && (
                              <p className="text-ink-3">
                                {b.startsAt &&
                                  t('admin.banners.startsInfo', { date: formatDate(b.startsAt, true) })}
                                {b.startsAt && b.endsAt && ' · '}
                                {b.endsAt &&
                                  t('admin.banners.endsInfo', { date: formatDate(b.endsAt, true) })}
                              </p>
                            )}
                            <TranslationLinks
                              items={b.translations}
                              to="/bannerlar/$id"
                              stateLabel={(st) => t(`admin.banners.states.${st}`)}
                            />
                          </div>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <Badge tone={stateTone[b.state]}>{t(`admin.banners.states.${b.state}`)}</Badge>
                            {b.audience !== 'all' && (
                              <Badge tone="slate">{t(`admin.banners.audiences.${b.audience}`)}</Badge>
                            )}
                            {b.reviewStatus !== 'not_required' && (
                              <Badge tone={reviewTone[b.reviewStatus]}>
                                {t(`admin.pages.reviews.${b.reviewStatus}`)}
                              </Badge>
                            )}
                          </span>
                          {canPublish && items.length > 1 && (
                            <span className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={i === 0 || reorder.isPending}
                                onClick={() => move(placement, items, i, -1)}
                                aria-label={t('admin.banners.moveUp', { name: b.name })}
                              >
                                <ArrowUp className="h-4 w-4" aria-hidden />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={i === items.length - 1 || reorder.isPending}
                                onClick={() => move(placement, items, i, 1)}
                                aria-label={t('admin.banners.moveDown', { name: b.name })}
                              >
                                <ArrowDown className="h-4 w-4" aria-hidden />
                              </Button>
                              <HelpTip k="banners.move" />
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </Section>
              );
            })
          )}
        </>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici

interface Draft {
  placement: Placement;
  name: string;
  title: string;
  text: string;
  ctaLabel: string;
  href: string;
  desktopFileId: string | null;
  mobileFileId: string | null;
  imageAlt: string;
  categoryIds: string[];
  audience: Audience;
  startsAt: string;
  endsAt: string;
  dismissDays: number;
}

const emptyDraft = (): Draft => ({
  placement: 'home_hero',
  name: '',
  title: '',
  text: '',
  ctaLabel: '',
  href: '',
  desktopFileId: null,
  mobileFileId: null,
  imageAlt: '',
  categoryIds: [],
  audience: 'all',
  startsAt: '',
  endsAt: '',
  dismissDays: 7,
});

function draftOf(b: BannerDetail): Draft {
  return {
    placement: b.placement,
    name: b.name,
    title: b.title ?? '',
    text: b.text ?? '',
    ctaLabel: b.ctaLabel ?? '',
    href: b.href ?? '',
    desktopFileId: b.desktopFileId,
    mobileFileId: b.mobileFileId,
    imageAlt: b.imageAlt ?? '',
    categoryIds: b.categoryIds ?? [],
    audience: b.audience,
    startsAt: toLocalInput(b.startsAt),
    endsAt: toLocalInput(b.endsAt),
    dismissDays: b.dismissDays,
  };
}

export function BannerEditorPage() {
  const { id } = useParams({ from: '/app/bannerlar/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['banner', id],
    queryFn: () => api.get<BannerDetail>(`/v1/admin/banners/${id}`),
    enabled: !isNew,
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <BannerEditor key={q.data?.id ?? 'yeni'} banner={q.data ?? null} />;
}

function BannerEditor({ banner }: { banner: BannerDetail | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const canPublish = can('content.publish');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = banner?.storeId ?? chosenStore;
  // Yeni banner seçili dilde açılır (listede seçilen dil hatırlanır).
  const { locales, locale: chosenLocale, setLocale } = useContentLocale(banner ? null : storeId);
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled: can('catalog.product.read'),
  });
  const [draft, setDraft] = useState<Draft>(() => (banner ? draftOf(banner) : emptyDraft()));
  useEffect(() => {
    if (banner) setDraft(draftOf(banner));
  }, [banner]);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = !banner || JSON.stringify(draft) !== JSON.stringify(draftOf(banner));
  const isActive = banner?.status === 'active';
  // Etkin banner vitrinde hemen değişir: yayınlama yetkisi ister.
  const canSave = canWrite && (!isActive || canPublish);
  const body = () => ({
    placement: draft.placement,
    name: draft.name,
    title: draft.title || null,
    text: draft.text || null,
    ctaLabel: draft.ctaLabel || null,
    href: draft.href || null,
    desktopFileId: draft.placement === 'announcement' ? null : draft.desktopFileId,
    mobileFileId: draft.placement === 'announcement' ? null : draft.mobileFileId,
    imageAlt: draft.placement === 'announcement' ? null : draft.imageAlt || null,
    categoryIds: draft.placement === 'category_top' ? draft.categoryIds : [],
    audience: draft.audience,
    startsAt: fromLocalInput(draft.startsAt),
    endsAt: fromLocalInput(draft.endsAt),
    dismissDays: draft.dismissDays,
  });
  const save = useApiMutation(
    () =>
      banner
        ? api.put<BannerDetail>(`/v1/admin/banners/${banner.id}`, { ...body(), version: banner.version })
        : api.post<BannerDetail>('/v1/admin/banners', {
            ...body(),
            storeId,
            locale: chosenLocale ?? undefined,
          }),
    {
      invalidate: [['banners'], ...(banner ? [['banner', banner.id]] : [])],
      success: t('admin.banners.saved'),
      onSuccess: (r) => {
        if (!banner) void navigate({ to: '/bannerlar/$id', params: { id: r.id } });
      },
    },
  );
  const status = useApiMutation(
    (action: 'activate' | 'deactivate') =>
      api.post<BannerDetail>(`/v1/admin/banners/${banner!.id}/${action}`, { version: banner!.version }),
    {
      invalidate: [['banners'], ['banner', banner?.id]],
      success: (r) => (r.status === 'active' ? t('admin.banners.activated') : t('admin.banners.deactivated')),
    },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<BannerDetail>(`/v1/admin/banners/${banner!.id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: banner!.contentHash,
        version: banner!.version,
      }),
    { invalidate: [['banners'], ['banner', banner?.id]], success: t('admin.pages.reviewedToast') },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/banners/${banner!.id}`), {
    invalidate: [['banners']],
    success: t('common.deleted'),
    onSuccess: () => void navigate({ to: '/bannerlar' }),
  });
  const translate = useApiMutation(
    (locale: string) => api.post<BannerDetail>(`/v1/admin/banners/${banner!.id}/translations`, { locale }),
    {
      invalidate: [['banners'], ['banner', banner?.id]],
      success: t('admin.translations.created'),
      silentFieldErrors: true,
      onSuccess: (r) => void navigate({ to: '/bannerlar/$id', params: { id: r.id } }),
    },
  );
  const err = save.error;
  const fe = (f: string) => fieldError(err, f);
  const withImage = IMAGE_PLACEMENTS.includes(draft.placement) || draft.placement === 'popup';
  return (
    <PageFrame
      helpKey="page.banner_editor"
      title={banner ? banner.name : t('admin.banners.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <>
              <Button
                variant={banner ? 'secondary' : 'primary'}
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !canSave || save.isPending}
              >
                {banner ? t('admin.banners.save') : t('admin.banners.createConfirm')}
              </Button>
              <HelpTip k="banners.save" />
            </>
          )}
          {banner && canPublish && (
            <>
              {isActive ? (
                <Button onClick={() => status.mutate('deactivate')} disabled={status.isPending}>
                  {t('admin.banners.deactivate')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => status.mutate('activate')}
                  disabled={status.isPending || dirty}
                >
                  {t('admin.banners.activate')}
                </Button>
              )}
              <HelpTip k={isActive ? 'banners.deactivate' : 'banners.activate'} />
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/bannerlar" className="text-pine hover:underline">
          {t('admin.banners.back')}
        </Link>
        {banner && <Badge tone={stateTone[banner.state]}>{t(`admin.banners.states.${banner.state}`)}</Badge>}
        {banner && dirty && <Badge tone="amber">{t('admin.banners.unsaved')}</Badge>}
        {banner && !isActive && dirty && canPublish && (
          <span className="text-ink-2">{t('admin.banners.saveBeforeActivate')}</span>
        )}
        {isActive && canWrite && !canPublish && (
          <span className="text-ink-2">{t('admin.banners.activeLocked')}</span>
        )}
      </div>
      {banner ? (
        <EditorLanguages
          storeId={banner.storeId}
          locale={banner.locale}
          translations={banner.translations}
          to="/bannerlar/$id"
          stateLabel={(st) => t(`admin.banners.states.${st}`)}
          canTranslate={canWrite}
          pending={translate.isPending}
          error={fieldError(translate.error, 'locale')}
          onCreate={(l) => translate.mutate(l)}
        />
      ) : (
        <div className="flex flex-wrap items-end gap-4">
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <LocalePicker locales={locales} locale={chosenLocale} onChange={setLocale} />
        </div>
      )}

      <fieldset disabled={!canSave} className="space-y-8">
        <Section title={t('admin.banners.basics')} className="border-t-0 pt-0">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={t('admin.banners.placement')} help={<HelpTip k="banners.placement" />}>
              {(p) => (
                <Select
                  {...p}
                  value={draft.placement}
                  onChange={(e) => set({ placement: e.target.value as Placement })}
                >
                  {PLACEMENTS.map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.banners.placements.${x}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.banners.name')}
              help={<HelpTip k="banners.name" />}
              description={t('admin.banners.nameLead')}
              error={fe('name')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.name}
                  maxLength={80}
                  onChange={(e) => set({ name: e.target.value })}
                />
              )}
            </Field>
            {draft.placement !== 'announcement' && (
              <Field
                label={t('admin.banners.title')}
                optionalLabel={draft.placement === 'popup' ? undefined : t('common.optional')}
                help={<HelpTip k="banners.title" />}
                error={fe('title')}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.title}
                    maxLength={120}
                    onChange={(e) => set({ title: e.target.value })}
                  />
                )}
              </Field>
            )}
            <Field
              label={
                draft.placement === 'announcement'
                  ? t('admin.banners.announcementText')
                  : t('admin.banners.text')
              }
              optionalLabel={
                ['announcement', 'popup'].includes(draft.placement) ? undefined : t('common.optional')
              }
              help={<HelpTip k="banners.text" />}
              description={t('admin.pages.chars', { count: draft.text.length, max: 300 })}
              error={fe('text')}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={draft.text}
                  maxLength={300}
                  rows={2}
                  onChange={(e) => set({ text: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.banners.ctaLabel')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="banners.cta" />}
              error={fe('ctaLabel')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.ctaLabel}
                  maxLength={40}
                  onChange={(e) => set({ ctaLabel: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.banners.href')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="banners.href" />}
              error={fe('href')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.href}
                  placeholder="/urunler"
                  spellCheck={false}
                  onChange={(e) => set({ href: e.target.value })}
                />
              )}
            </Field>
          </div>
        </Section>

        {withImage && (
          <Section title={t('admin.banners.images')} description={t('admin.banners.imagesLead')}>
            <div className="grid gap-4">
              <div>
                <p className="mb-1.5 text-sm font-medium">
                  {t('admin.banners.desktopImage')}
                  {draft.placement === 'popup' && (
                    <span className="ms-1 font-normal text-ink-3">({t('common.optional')})</span>
                  )}
                </p>
                <ImageUpload
                  wide
                  value={draft.desktopFileId}
                  label={
                    draft.desktopFileId ? t('admin.banners.replaceDesktop') : t('admin.banners.uploadDesktop')
                  }
                  removable={draft.placement === 'popup'}
                  help={<HelpTip k="banners.desktop_image" />}
                  onChange={(desktopFileId) => set({ desktopFileId })}
                />
                {fe('desktopFileId') && (
                  <p role="alert" className="mt-1 text-sm font-medium text-rose">
                    {fe('desktopFileId')}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1.5 text-sm font-medium">
                  {t('admin.banners.mobileImage')}
                  <span className="ms-1 font-normal text-ink-3">({t('common.optional')})</span>
                </p>
                <ImageUpload
                  value={draft.mobileFileId}
                  label={
                    draft.mobileFileId ? t('admin.banners.replaceMobile') : t('admin.banners.uploadMobile')
                  }
                  removable
                  help={<HelpTip k="banners.mobile_image" />}
                  onChange={(mobileFileId) => set({ mobileFileId })}
                />
                {fe('mobileFileId') && (
                  <p role="alert" className="mt-1 text-sm font-medium text-rose">
                    {fe('mobileFileId')}
                  </p>
                )}
              </div>
              {(draft.desktopFileId || draft.mobileFileId) && (
                <Field
                  label={t('admin.pages.imageAlt')}
                  help={<HelpTip k="banners.image_alt" />}
                  error={fe('imageAlt')}
                  required
                  className="max-w-xl"
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={draft.imageAlt}
                      maxLength={300}
                      onChange={(e) => set({ imageAlt: e.target.value })}
                    />
                  )}
                </Field>
              )}
            </div>
          </Section>
        )}

        <Section title={t('admin.banners.targeting')} description={t('admin.banners.targetingLead')}>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label={t('admin.banners.audience')} help={<HelpTip k="banners.audience" />}>
              {(p) => (
                <Select
                  {...p}
                  value={draft.audience}
                  onChange={(e) => set({ audience: e.target.value as Audience })}
                >
                  {AUDIENCES.map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.banners.audiences.${x}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.banners.startsAt')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="banners.dates" />}
            >
              {(p) => (
                <Input
                  {...p}
                  type="datetime-local"
                  value={draft.startsAt}
                  onChange={(e) => set({ startsAt: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.banners.endsAt')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="banners.dates" />}
              error={fe('endsAt')}
            >
              {(p) => (
                <Input
                  {...p}
                  type="datetime-local"
                  value={draft.endsAt}
                  onChange={(e) => set({ endsAt: e.target.value })}
                />
              )}
            </Field>
            {draft.placement === 'popup' && (
              <Field
                label={t('admin.banners.dismissDays')}
                help={<HelpTip k="banners.dismiss_days" />}
                error={fe('dismissDays')}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    max={365}
                    value={draft.dismissDays}
                    onChange={(e) =>
                      set({ dismissDays: Math.max(1, Math.min(365, Number(e.target.value) || 7)) })
                    }
                  />
                )}
              </Field>
            )}
          </div>
          {draft.placement === 'category_top' && (
            <fieldset className="mt-4">
              <legend className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                {t('admin.banners.categories')}
                <HelpTip k="banners.categories" />
              </legend>
              <p className="mb-2 text-sm text-ink-2">{t('admin.banners.categoriesLead')}</p>
              {fe('categoryIds') && (
                <p role="alert" className="mb-1 text-sm font-medium text-rose">
                  {fe('categoryIds')}
                </p>
              )}
              <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                {(cats.data ?? []).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={draft.categoryIds.includes(c.id)}
                      onChange={(e) =>
                        set({
                          categoryIds: e.target.checked
                            ? [...draft.categoryIds, c.id]
                            : draft.categoryIds.filter((x) => x !== c.id),
                        })
                      }
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        </Section>
      </fieldset>

      {banner && (
        <ContentReviewPanel
          status={banner.reviewStatus}
          note={banner.reviewNote}
          findings={banner.findings}
          required={banner.requiresReview}
          dirty={dirty}
          canReview={can('content.expert_review')}
          busy={review.isPending}
          onDecide={(decision, note) => review.mutate({ decision, note })}
        />
      )}

      {banner && canPublish && !isActive && (
        <Section title={t('admin.banners.dangerZone')}>
          <div className="flex items-center gap-2">
            <ConfirmButton
              message={t('admin.banners.deleteConfirm')}
              onConfirm={() => remove.mutateAsync(undefined)}
            >
              {t('common.delete')}
            </ConfirmButton>
            <HelpTip k="banners.delete" />
          </div>
        </Section>
      )}
      {banner && (
        <p className="text-sm text-ink-3">
          {t('admin.banners.updated', { date: formatDate(banner.updatedAt, true) })}
        </p>
      )}
    </PageFrame>
  );
}
